/**
 * rebookNotices — what the rebook drawer is currently refusing, warning or
 * prompting about, and whether any of it blocks the move (BON-146).
 *
 * Pure and framework-free so the branching is unit-testable without mounting a
 * drawer. Notices are returned as a DISCRIMINATED MODEL, not as rendered copy:
 * two of them need interactive innards (the override checkbox, the link to the
 * successor), so the shape has to survive the trip to a component. The words
 * live in `RebookNoticeCard`.
 *
 * The distinction that matters is `blocking`. A blocked notice means nothing the
 * CSR can do in this drawer will help — the booking is gone, or the cutoff has
 * passed and they cannot override it — so the submit is disabled. A non-blocking
 * one means try again differently: pick another workshop, tick the box, retry.
 * Getting that backwards either strands a CSR on a live booking or lets them
 * hammer a write that cannot succeed.
 */
import { RebookBookingError, type RebookErrorDetail } from '../../api/rebooking';

export type RebookNoticeTone = 'block' | 'warn';

/** One notice, in the order it should render. */
export interface RebookNotice {
  /** Stable key. */
  id: string;
  tone: RebookNoticeTone;
  /** True → the submit button is disabled while this is showing. */
  blocking: boolean;
  detail: RebookNoticeDetail;
}

export type RebookNoticeDetail =
  /** Past the 10 AM cutoff and the caller cannot override. Reachable only as a
   *  RACE — the button is disabled for this case — when the cutoff passes
   *  between page load and submit. */
  | { kind: 'cutoffBlocked'; workshopWhen: string }
  /** Past the cutoff, caller holds `override:rebook-cutoff`. Carries the
   *  checkbox; blocking until it is ticked. */
  | { kind: 'cutoffOverride'; workshopWhen: string; checked: boolean }
  /** The booking moved under us (another tab, or a double submit that lost). */
  | {
      kind: 'alreadyRebooked';
      successorBookingId?: string;
      successorConfirmationCode?: string | null;
    }
  /** Not confirmed — carries the REAL status so a refunded booking reads as
   *  refunded rather than as a vague "not confirmed". */
  | { kind: 'notConfirmed'; status?: string }
  /** The chosen workshop filled up between listing and submit. */
  | { kind: 'noCapacity'; available?: number; requested?: number }
  /** Submit pressed with nothing chosen. */
  | { kind: 'noPick' }
  /** The write failed for a reason the CSR can retry — a 5xx, or a refusal
   *  about the TARGET rather than the booking. */
  | { kind: 'failure'; message: string; requestId?: string };

export interface RebookNoticeInput {
  /** Server verdict from the targets envelope. Never a client clock. */
  cutoffPassed: boolean;
  /** The caller holds `override:rebook-cutoff`. */
  canOverrideCutoff: boolean;
  /** The CSR ticked the explicit opt-in. */
  overrideChecked: boolean;
  /** Venue-local "Tue May 19 at 6:30p" for the workshop being left. */
  workshopWhen: string;
  /** Submit has been pressed at least once — gates the "pick one" prompt so it
   *  does not greet a CSR who has not tried anything yet. */
  attempted: boolean;
  hasPick: boolean;
  /** Whatever the last submit threw, or null. */
  error: unknown;
}

/**
 * Refusal code → the notice it becomes. A TABLE rather than a chain of `if`s, in
 * the same order as the contract's refusal list, so a reviewer can read it
 * against that list line by line — and so adding a code cannot quietly land in
 * the generic branch, which is how a cutoff refusal came to render as a
 * retryable failure with the button still live.
 *
 * A code absent from this table falls through to the generic failure, which is
 * correct for a genuine 500 and wrong for anything the server refuses
 * deliberately. If you add a refusal code to the API, add it here too.
 */
const NOTICE_BY_CODE: Record<
  string,
  (detail: RebookErrorDetail, workshopWhen: string) => RebookNotice
> = {
  ALREADY_REBOOKED: (detail) => ({
    id: 'alreadyRebooked',
    tone: 'block',
    blocking: true,
    detail: {
      kind: 'alreadyRebooked',
      successorBookingId: detail.successorBookingId,
      successorConfirmationCode: detail.successorConfirmationCode,
    },
  }),
  NOT_CONFIRMED: (detail) => ({
    id: 'notConfirmed',
    tone: 'block',
    blocking: true,
    detail: { kind: 'notConfirmed', status: detail.bookingStatus },
  }),
  NO_CAPACITY: (detail) => ({
    id: 'noCapacity',
    tone: 'block',
    // Not blocking: picking another workshop is the fix, and it is in this drawer.
    blocking: false,
    detail: { kind: 'noCapacity', available: detail.available, requested: detail.requested },
  }),
  // A cutoff refusal from the SERVER means the envelope's `cutoffPassed` was
  // stale — the CSR opened the drawer before 10am and submitted after it.
  PAST_CUTOFF: (_detail, workshopWhen) => ({
    id: 'cutoff',
    tone: 'block',
    blocking: true,
    detail: { kind: 'cutoffBlocked', workshopWhen },
  }),
  OVERRIDE_NOT_PERMITTED: (_detail, workshopWhen) => ({
    id: 'cutoff',
    tone: 'block',
    blocking: true,
    detail: { kind: 'cutoffBlocked', workshopWhen },
  }),
};

function errorNotice(error: unknown, workshopWhen: string): RebookNotice | null {
  if (error === null || error === undefined) return null;
  if (!(error instanceof RebookBookingError)) {
    const message = error instanceof Error ? error.message : 'Could not move this booking.';
    return { id: 'failure', tone: 'block', blocking: false, detail: { kind: 'failure', message } };
  }
  const { code, detail, message } = error;
  const build = code ? NOTICE_BY_CODE[code] : undefined;
  if (build) return build(detail, workshopWhen);
  return {
    id: 'failure',
    tone: 'block',
    // Anything reaching here is an unrecognised code or a genuine 500, so it is
    // retryable by definition — every deliberate refusal is in the table above.
    blocking: false,
    detail: { kind: 'failure', message, requestId: detail.requestId },
  };
}

/**
 * Notices in render order: the freshest reason first. A failed submit outranks
 * the standing cutoff warning — the CSR just pressed the button and needs to
 * know what came back, not be re-told a condition they already read and
 * accepted.
 */
export function deriveRebookNotices(input: RebookNoticeInput): RebookNotice[] {
  const notices: RebookNotice[] = [];

  const failure = errorNotice(input.error, input.workshopWhen);
  if (failure) notices.push(failure);

  if (input.attempted && !input.hasPick) {
    notices.push({ id: 'noPick', tone: 'block', blocking: false, detail: { kind: 'noPick' } });
  }

  // Ordering rule, the same one `rebookAffordance` gets right: a booking that is
  // already gone must not be blamed on the cutoff. Asking a manager to override
  // would change nothing, and the override checkbox would be live and useless.
  const terminal = notices.some(
    (n) => n.detail.kind === 'alreadyRebooked' || n.detail.kind === 'notConfirmed',
  );

  if (terminal) {
    // fall through — no cutoff notice
  } else if (input.cutoffPassed && !input.canOverrideCutoff) {
    notices.push({
      id: 'cutoff',
      tone: 'block',
      blocking: true,
      detail: { kind: 'cutoffBlocked', workshopWhen: input.workshopWhen },
    });
  } else if (input.cutoffPassed) {
    notices.push({
      id: 'cutoff',
      // Warn, not block, once ticked: the CSR has read it and chosen to proceed,
      // and a red stop sign above a button they are about to press correctly
      // just teaches them to ignore red stop signs.
      tone: input.overrideChecked ? 'warn' : 'block',
      blocking: !input.overrideChecked,
      detail: {
        kind: 'cutoffOverride',
        workshopWhen: input.workshopWhen,
        checked: input.overrideChecked,
      },
    });
  }

  return notices;
}

export function isBlocked(notices: RebookNotice[]): boolean {
  return notices.some((n) => n.blocking);
}
